import diff from 'fast-diff';
import { ReactNode } from 'react';

/**
 * Returns a ReactNode array representing the highlighted diff between two strings.
 *
 * The diff visually indicates the changes needed to transform the source string into the target string:
 * - Removed text (present in source but not in target) is wrapped in a <span> with class "highlighted-diff-summary removed"
 * - Added text (present in target but not in source) is wrapped in a <span> with class "highlighted-diff-summary added"
 * - Unchanged text is rendered as plain text
 *
 * @param {string} source - The original string to compare from.
 * @param {string} target - The new string to compare to.
 * @returns {ReactNode[]} An array of React nodes representing the highlighted diff.
 *
 * @example
 * // Comparing "Hello world" to "Hello earth"
 * const diff = getHighlightedDiffBetweenStrings('Hello world', 'Hello earth');
 * // diff will be: [
 * //   "Hello ",
 * //   <span className="highlighted-diff-summary removed">world</span>,
 * //   <span className="highlighted-diff-summary added">earth</span>
 * // ]
 */
export function getHighlightedDiffBetweenStrings(source: string, target: string): ReactNode[] {
    // Generate diff operations array by comparing source to target
    // Each element is [operation, text] where operation indicates:
    // - diff.DELETE (-1): text exists in source but not target (was removed)
    // - diff.INSERT (1): text exists in target but not source (was added)
    // - diff.EQUAL (0): text exists in both strings (unchanged)
    const diffOperations = diff(source, target);

    // Build the highlighted diff summary string
    let highlightedDiff: ReactNode[] = [];

    // Process each diff operation to build the visual representation
    diffOperations.forEach(([operation, text]) => {
        if (operation === diff.DELETE) {
            // Text was removed when going from source to target
            // Example: "world" in "Hello world" → "Hello earth"
            highlightedDiff.push(<span className="highlighted-diff-summary removed">{text}</span>);
        } else if (operation === diff.INSERT) {
            // Text was added when going from source to target
            // Example: "earth" in "Hello world" → "Hello earth"
            highlightedDiff.push(<span className="highlighted-diff-summary added">{text}</span>);
        } else if (operation === diff.EQUAL) {
            // Text appears in both strings unchanged
            // Example: "Hello " in "Hello world" → "Hello earth"
            // Show normally without any special styling
            highlightedDiff.push(text);
        }
    });

    return highlightedDiff;
}
